"use client";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Field } from "@/components/ui/field";
import { FieldError } from "@/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useDebounce } from "@/hooks/use-debounce";
import {
  IHotelLocation,
  useSearchHotelLocationQuery,
} from "@/lib/api/common-api";
import { cn } from "@/lib/utils";
import { Check, Loader2, MapPin } from "lucide-react";
import { useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";

import { Badge } from "@/components/ui/badge";
import {
  IHotelsSearchSchema,
  IIHotelListSchema,
} from "../../types/hotel-search-type";

interface HotelLocationSelectProps {
  form: UseFormReturn<IHotelsSearchSchema>;
  popoverModal?: boolean;
  disabled?: boolean;
}

export default function HotelLocationSelect({
  form,
  popoverModal = false,
  disabled = false,
}: HotelLocationSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { data, isLoading, isFetching } = useSearchHotelLocationQuery(
    {
      filter: debouncedSearchTerm,
      limit: 20,
      skip: 0,
    },
    {
      refetchOnReconnect: false,
      refetchOnMountOrArgChange: true,
    },
  );

  const hotelList = (data?.data ?? []) as IHotelLocation[];

  return (
    <Controller
      control={form.control}
      name="location"
      render={({ field, fieldState }) => {
        const selectedHotel = field.value as IIHotelListSchema | undefined;
        const selectedId = selectedHotel?.id;
        const hasError = fieldState.invalid || Boolean(fieldState.error);

        return (
          <Field data-invalid={hasError}>
            <Popover open={open} modal={popoverModal} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  role="combobox"
                  aria-expanded={open}
                  aria-invalid={hasError}
                  disabled={disabled}
                  className={cn(
                    "h-14 w-full justify-between rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40 sm:h-[56px]",
                    !selectedHotel?.name && "text-muted-foreground",
                    hasError && "border-destructive",
                    disabled &&
                      "cursor-not-allowed bg-muted/25 text-foreground opacity-100 hover:bg-muted/25",
                  )}
                >
                  {selectedHotel?.name ? (
                    <span className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
                      <span className="text-base font-bold tracking-tight text-foreground sm:text-[1.05rem]">
                        {(
                          selectedHotel.type?.slice(0, 3) ??
                          selectedHotel.name.slice(0, 3)
                        ).toUpperCase()}
                      </span>
                      <span className="h-9 w-px bg-border" />
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-foreground/85">
                          {selectedHotel.name}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {[selectedHotel.country_name, selectedHotel.type]
                            .filter(Boolean)
                            .join(", ")}
                        </span>
                      </span>
                    </span>
                  ) : (
                    <span className="min-w-0 text-left">
                      <span className="block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
                        Destination
                      </span>
                      <span className="block truncate text-sm text-muted-foreground">
                        Select location
                      </span>
                    </span>
                  )}
                </Button>
              </PopoverTrigger>

              <PopoverContent className="p-0 h-52 md:h-full" align="start">
                <Command shouldFilter={false} className="relative">
                  <CommandInput
                    placeholder="Search hotels by name, city or country..."
                    value={searchTerm}
                    onValueChange={setSearchTerm}
                  />
                  {(isLoading || isFetching) && (
                    <Loader2 className="text-muted-foreground absolute right-2 top-2 h-4 w-4 animate-spin" />
                  )}

                  <CommandList className="no-scrollbar">
                    <CommandEmpty>
                      {isLoading || isFetching
                        ? "Loading..."
                        : "No hotel found"}
                    </CommandEmpty>
                    {hotelList.length > 0 && (
                      <CommandGroup>
                        {hotelList.map((item) => {
                          const isSelected = selectedId === item.id;

                          return (
                            <CommandItem
                              key={item.id}
                              className={cn(
                                "w-full cursor-pointer truncate border-b px-2 py-2",
                                isSelected &&
                                  "bg-primary text-primary-foreground",
                              )}
                              value={String(item.id)}
                              onSelect={() => {
                                field.onChange(item);
                                setOpen(false);
                              }}
                            >
                              <div className="flex w-full justify-between">
                                <div className="flex-[2] truncate tracking-tight">
                                  <h3
                                    className={cn(
                                      "font-medium",
                                      isSelected
                                        ? "text-white"
                                        : "text-primary",
                                    )}
                                  >
                                    {item.name}
                                  </h3>

                                  <p className="flex items-center gap-1 truncate text-xs">
                                    <MapPin
                                      className={cn(
                                        "text-primary size-3",
                                        isSelected && "text-white",
                                      )}
                                    />
                                    {item.name}, {item.country_name}
                                  </p>
                                </div>

                                <div className="flex flex-1 items-center justify-end">
                                  <div className="flex flex-col items-end gap-1">
                                    <Badge
                                      className="text-xs"
                                      variant="secondary"
                                    >
                                      {item.type}
                                    </Badge>
                                    <Check
                                      className={cn(
                                        "h-4 w-4",
                                        isSelected
                                          ? "opacity-100"
                                          : "opacity-0",
                                      )}
                                    />
                                  </div>
                                </div>
                              </div>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FieldError errors={[fieldState.error]} />
          </Field>
        );
      }}
    />
  );
}
